// @flow

/**
 * Download Heading Utilities
 *
 * Centralizes generation of heading text inserted into exported PDF/DOCX content.
 *
 * Requirements:
 * - Include local date + time of download
 * - Date uses ISO-like YYYY-MM-DD
 * - Time uses browser locale preferences (12/24h, separators, AM/PM)
 */

export type DownloadHeadingKind = 'message' | 'thread';

const stripBidiMarks = (value: string): string => value.replace(/[\u200E\u200F\u202A-\u202E]/g, '');

export const normalizeSingleLine = (value: ?string): string => {
  return String(value || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const formatDownloadTimestamp = (now: Date = new Date(), locale?: string): string => {
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  // Use Intl to respect browser preferences for time formatting.
  // We remove bidi marks and normalize whitespace for stable rendering.
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const time = normalizeSingleLine(stripBidiMarks(timeFormatter.format(now)));

  return `${year}-${month}-${day} ${time}`;
};

/**
 * Timestamp formatted for filenames (no spaces/colons), local time.
 * Example: 2026-01-20_14-37
 */
export const formatDownloadTimestampForFilename = (now: Date = new Date()): string => {
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}_${hours}-${minutes}`;
};

export const buildDownloadHeading = (params: {
  kind: DownloadHeadingKind,
  now?: Date,
  locale?: string,
  title?: ?string,
  fallbackTitle?: ?string,
}): string => {
  const timestamp = formatDownloadTimestamp(params.now || new Date(), params.locale);

  if (params.kind === 'message') {
    return `${timestamp} - Ask Aura Response`;
  }

  const title = normalizeSingleLine(params.title);
  const fallbackTitle = normalizeSingleLine(params.fallbackTitle);
  const finalTitle = title || fallbackTitle || 'Ask Aura Conversation';

  return `${timestamp} - ${finalTitle}`;
};

export const prependHeadingToMarkdown = (markdown: string, heading: string): string => {
  const normalizedHeading = normalizeSingleLine(heading);
  const body = String(markdown || '');

  // Use a level-1 markdown heading so it renders well in both converters.
  return `# ${normalizedHeading}\n\n${body}`;
};
